import React, { useContext } from 'react';
import classNames from 'classnames/bind';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { string, bool } from 'prop-types';
import {
  useDataModel,
  params,
  string as stringType,
  shape as shapeType
} from '@thd-nucleus/data-sources';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { Typography } from '@one-thd/sui-atomic-components';

const ONLINE_EXCLUSIVE = 'Online';
export const ProductInfoBar = ({ itemId, padded, showOnMobile, compactMobile }) => {
  const { configuredItemId } = useLifeCycleEventBus('configurator.stock_product_chosen_event');
  const actualItemId = configuredItemId || itemId;
  const { channel } = useContext(ExperienceContext);
  const theme = useTheme(ProductInfoBar, { itemId, showOnMobile });
  const { data } = useDataModel('product', { variables: { itemId: actualItemId } });
  if (!data?.product) {
    return null;
  }
  const isMobile = channel === 'mobile';
  const { showUpcCode } = theme?.props || {};
  const { product } = data || {};
  const { identifiers, availabilityType } = product || {};
  const {
    modelNumber,
    rentalCategory,
    rentalSubCategory,
    storeSkuNumber,
    specialOrderSku,
    toolRentalSkuNumber,
    upc,
    productType
  } = identifiers || {};
  const { type } = availabilityType || {};
  const isMajorAppliance = productType === 'MAJOR_APPLIANCE';
  const shouldHideStoreSku = (type === ONLINE_EXCLUSIVE && !isMajorAppliance);
  const productInfoBarClasses = classNames(['sui-flex sui-text-xs sui-flex-wrap'], {
    'sui-px-2': padded && !isMobile,
    'sui-pl-2': padded && isMobile,
    'sui-flex sui-mb-2 sui-ml-2': showOnMobile && compactMobile,
    'sui-flex-col': showOnMobile && !compactMobile
  });
  const labelVariant = isMobile && !compactMobile ? 'body-base' : 'body-xs';
  const labelWeight = isMobile && !compactMobile ? 'bold' : 'regular';
  const labelHeight = isMobile && !compactMobile ? 'loose' : 'normal';

  const numberVariant = isMobile && !compactMobile ? 'body-base' : 'body-xs';
  const numberWeight = 'regular';
  const numberHeight = isMobile && !compactMobile ? 'loose' : 'normal';

  const componentType = 'h2';

  return (
    <div className={productInfoBarClasses}>
      <div className="sui-flex sui-inline-flex sui-mr-2">
        <Typography
          variant={labelVariant}
          component={componentType}
          align="left"
          weight={labelWeight}
          height={labelHeight}
        >
          Internet #
        </Typography>
        <Typography
          variant={numberVariant}
          component={componentType}
          weight={numberWeight}
          align="left"
          height={numberHeight}
        >
          {actualItemId}
        </Typography>
      </div>
      {modelNumber
        && (
          <div className="sui-flex sui-inline-flex sui-mr-2">
            <Typography
              variant={labelVariant}
              component={componentType}
              align="left"
              weight={labelWeight}
              height={labelHeight}
            >
              Model #
            </Typography>
            <Typography
              variant={numberVariant}
              component={componentType}
              weight={numberWeight}
              align="left"
              height={numberHeight}
            >
              {modelNumber}
            </Typography>
          </div>
        )}
      {upc && showUpcCode
        && (
          <div className="sui-flex sui-inline-flex sui-mr-2">
            <Typography variant={labelVariant} align="left" weight={labelWeight} height={labelHeight}>
              UPC Code #
            </Typography>
            <Typography variant={numberVariant} weight={numberWeight} align="left" height={numberHeight}>
              {upc}
            </Typography>
          </div>
        )}
      {storeSkuNumber && !shouldHideStoreSku
        && (
          <div className="sui-flex sui-inline-flex sui-mr-2">
            <Typography
              variant={labelVariant}
              component={componentType}
              align="left"
              weight={labelWeight}
              height={labelHeight}
            >
              Store SKU #
            </Typography>
            <Typography
              variant={numberVariant}
              component={componentType}
              weight={numberWeight}
              align="left"
              height={numberHeight}
            >
              {storeSkuNumber}
            </Typography>
          </div>
        )}
      {specialOrderSku
        && (
          <div className="sui-flex sui-inline-flex sui-mr-2">
            <Typography variant={labelVariant} align="left" weight={labelWeight} height={labelHeight}>
              Store SO SKU #
            </Typography>
            <Typography variant={numberVariant} weight={numberWeight} align="left" height={numberHeight}>
              {specialOrderSku}
            </Typography>
          </div>
        )}
      {toolRentalSkuNumber
        && (
          <div className="sui-flex sui-inline-flex sui-mr-2">
            <Typography variant={labelVariant} align="left" weight={labelWeight} height={labelHeight}>
              Rental SKU #
            </Typography>
            <Typography variant={numberVariant} weight={numberWeight} align="left" height={numberHeight}>
              {toolRentalSkuNumber}
            </Typography>
          </div>
        )}
      {rentalCategory && rentalSubCategory && (
        <>
          <div className="sui-flex sui-inline-flex sui-mr-2">
            <Typography variant={labelVariant} align="left" weight={labelWeight} height={labelHeight}>
              Rental Category #
            </Typography>
            <Typography variant={numberVariant} weight={numberWeight} align="left" height={numberHeight}>
              {rentalCategory}
            </Typography>
          </div>
          <div className="sui-flex sui-inline-flex sui-mr-2">
            <Typography variant={labelVariant} align="left" weight={labelWeight} height={labelHeight}>
              Rental Subcategory #
            </Typography>
            <Typography variant={numberVariant} weight={numberWeight} align="left" height={numberHeight}>
              {rentalSubCategory}
            </Typography>
          </div>
        </>
      )}
    </div>
  );
};

ProductInfoBar.themeProps = {
  showUpcCode: bool
};

ProductInfoBar.defaultThemeProps = {
  showUpcCode: false
};

ProductInfoBar.displayName = 'ProductInfoBar';

ProductInfoBar.defaultProps = {
  padded: false,
  showOnMobile: false,
  compactMobile: false,
};

ProductInfoBar.propTypes = {
  padded: bool,
  itemId: string.isRequired,
  showOnMobile: bool,
  compactMobile: bool,
};

ProductInfoBar.dataModel = {
  product: params({ itemId: stringType().isRequired(), dataSource: stringType() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    identifiers: shapeType({
      modelNumber: stringType(),
      storeSkuNumber: stringType(),
      specialOrderSku: stringType(),
      toolRentalSkuNumber: stringType(),
      rentalCategory: stringType(),
      rentalSubCategory: stringType(),
      upc: stringType(),
      productType: stringType(),
    }),
    availabilityType: shapeType({
      type: stringType()
    })
  })
};
